import { useTheme } from '@src/theme/useTheme';
import { OrganizerFace } from '@src/components/faces/OrganizerFace';
import { organizerMessages } from './organizerMessages';
import { GamePageShell } from './GamePageShell';
import { useMobileBoxLayout } from './BoxLayouts';
import { NotificationBadge } from '@src/components/NotificationBadge/NotificationBadge';
import { loadConversation, useMessageInbox } from './MessageInbox';
import { useNavigate } from 'react-router-dom';
import { useLayoutEffect, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  isPlaceholderOrganizerUsername,
  type BoxInfo,
  type CardInfo,
  type FullCardInfo,
  type MessageInfo,
} from '@lub0v/santa-utils';
import {
  Button,
  Note,
  Panel,
  MailIcon,
  ParticipantFace,
  SantaAvatar,
  UserIcon,
  Stack,
  Textarea,
  Typography,
  showToast,
} from '@src/components';

import { boxMessengerPath } from '@src/routing/routes';
import { santaClient } from '@src/state/client';

import { sortOrganizerCards } from '@src/pages/BoxPage/sortCards';
import {
  FileUpload,
  type FileUploadHandle,
  type UploadedFile,
} from '@src/components/FileUpload/FileUpload';
import { BoxPicture } from '@src/components/BoxPicture/BoxPicture';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { ChevronRightIcon } from '@src/components/icons/ChevronRightIcon';
import { ArrowRightIcon } from '@src/components/icons/ArrowRightIcon';
import { AttachmentIcon } from '@src/components/icons/AttachmentIcon';
import { canEditMessage } from './messageEditWindow';
import { MessageBubble } from './MessageBubble';
import { MessageAttachment } from './MessageAttachment';
import { MobileLandscapeBackground } from '@src/components/Landscape/MobileLandscapeBackground';
import './MessengerView.css';

type ThreadId = 'org' | 'giftee' | 'me' | `user-${number}` | `org-${number}`;

interface Thread {
  id: ThreadId;
  title: string;
  hint: string;
  cardId?: number;
}

export interface MessengerViewProps {
  info: BoxInfo;
  cards: CardInfo[];
  card: FullCardInfo | null;
  ward: FullCardInfo | null;
}

function parseSelected(value: string | null): ThreadId | null {
  if (value === 'org' || value === 'giftee' || value === 'me') {
    return value;
  }
  const match = value?.match(/^(user|org)-(\d+)$/);
  return match ? (`${match[1]}-${Number(match[2])}` as ThreadId) : null;
}

export function MessengerView(props: MessengerViewProps) {
  const [search] = useSearchParams();
  return (
    <MessengerContent key={`${props.info.box.key}:${search.get('selected') || ''}`} {...props} />
  );
}

function MessengerContent({ info, cards, card, ward }: MessengerViewProps) {
  const navigate = useNavigate();
  const mobile = useMobileBoxLayout();
  const inbox = useMessageInbox();
  const inboxRef = useRef(inbox);
  useLayoutEffect(() => {
    inboxRef.current = inbox;
  }, [inbox]);
  const { t, i18n } = useTranslation();
  const isAlpine = useTheme().seasonPackage.id === 'alpine';
  const [search] = useSearchParams();
  const parsed = parseSelected(search.get('selected'));
  const defaultOrganizer = info.organizers?.find(person => !person.isYou);
  const selected = parsed === 'org' && defaultOrganizer ? `org-${defaultOrganizer.id}` as ThreadId : parsed;
  const [loadedMessages, setMessages] = useState<MessageInfo[]>([]);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ message: MessageInfo; previousDraft: string } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [pendingFile, setPendingFile] = useState<UploadedFile | null>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const fileUploadRef = useRef<FileUploadHandle>(null);
  const [loading, setLoading] = useState(Boolean(selected));
  const [loadError, setLoadError] = useState(false);
  const [recipientUnavailable, setRecipientUnavailable] = useState(false);
  const [reload, setReload] = useState(0);
  const historyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 48rem)');
    const viewport = window.visualViewport;
    const root = document.documentElement;
    function updateViewport() {
      root.classList.toggle('messenger-mobile-open', media.matches);
      root.style.setProperty(
        '--messenger-viewport-height',
        `${viewport?.height ?? window.innerHeight}px`,
      );
      root.style.setProperty('--messenger-viewport-top', `${viewport?.offsetTop ?? 0}px`);
    }
    updateViewport();
    media.addEventListener('change', updateViewport);
    viewport?.addEventListener('resize', updateViewport);
    viewport?.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);
    return () => {
      root.classList.remove('messenger-mobile-open');
      root.style.removeProperty('--messenger-viewport-height');
      root.style.removeProperty('--messenger-viewport-top');
      media.removeEventListener('change', updateViewport);
      viewport?.removeEventListener('resize', updateViewport);
      viewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
    };
  }, []);

  const archived = Boolean(info.box.isArchived);
  const organizerName = info.admin?.username?.trim();
  const organizerTitle =
    organizerName && !isPlaceholderOrganizerUsername(organizerName)
      ? t('game.messenger.namedOrganizer', { name: organizerName })
      : t('game.messenger.org');

  const threads = useMemo(() => {
    const list: Thread[] = [];
    if (info.isParticipant || info.isAdmin) {
      if (info.organizers?.length) info.organizers.filter(person => !person.isYou).forEach(person => list.push({
        id: `org-${person.id}`, title: t('game.messenger.namedOrganizer', { name: person.username }),
        hint: t('game.messenger.orgHint'), cardId: person.id,
      }));
      else list.push({ id: 'org', title: organizerTitle, hint: t('game.messenger.orgHint') });
    }
    if (info.isParticipant && info.isActiveDraw) {
      list.push({
        id: 'me',
        title: t('game.messenger.santa'),
        hint: t('game.messenger.santaHint', { name: card?.username || '' }),
      });
      if (card?.isWardKnown && ward) {
        list.push({
          id: 'giftee',
          title: t('game.messenger.giftee', { name: ward.username }),
          hint: t('game.messenger.gifteeHint'),
        });
      }
    }
    if (info.isAdmin) {
      cards
        .filter((item) => !item.isYou && !info.organizers?.some(person => person.id === item.id))
        .forEach((item) => {
          list.push({
            id: `user-${item.id}`,
            title: item.username || t('game.people.hidden'),
            hint: t('game.messenger.userHint'),
            cardId: item.id,
          });
        });
    }
    if (
      info.isAdmin &&
      selected?.startsWith('user-') &&
      !list.some((item) => item.id === selected)
    ) {
      list.push({
        id: selected,
        cardId: Number(selected.slice(5)),
        title: t('game.messenger.deletedCard'),
        hint: '',
      });
    }
    return list;
  }, [
    organizerTitle,
    info.organizers,
    selected,
    card?.isWardKnown,
    card?.username,
    cards,
    info.isActiveDraw,
    info.isAdmin,
    info.isParticipant,
    t,
    ward,
  ]);

  const availableThreads = threads.filter((item) => {
    const hasMessages = Boolean(inbox.summaries[item.id]);
    // An explicitly opened new chat remains available while the organizer writes its first message.
    if (info.isAdmin && item.id.startsWith('user-') && !hasMessages && item.id !== selected)
      return false;
    return true;
  });
  const visibleThreads = availableThreads;
  const thread = threads.find((item) => item.id === selected) ?? null;

  const selectedAvailable = threads.some((item) => item.id === selected);
  useEffect(() => {
    if (!selected || !selectedAvailable) return;
    let active = true;
    const key = info.box.key;
    const id = selected;
    let fetching = false;
    async function load(initial = false) {
      if (fetching || document.hidden) return;
      fetching = true;
      if (initial) setLoading(true);
      setLoadError(false);
      try {
        const res = await loadConversation(key, id);
        if (initial && id.startsWith('user-')) {
          const recipients = await santaClient.getCards({ key });
          if (!active) return;
          setRecipientUnavailable(
            !recipients.ok || !recipients.data?.some((item) => item.id === Number(id.slice(5))),
          );
        }
        if (!active) return;
        if (!res.ok || !res.data) {
          setLoadError(true);
          return;
        }
        setMessages(res.data);
        inboxRef.current.update(id, res.data);
      } catch {
        if (active) setLoadError(true);
      } finally {
        fetching = false;
        if (active) setLoading(false);
      }
    }
    const initialTimer = window.setTimeout(() => { void load(true); }, 0);
    const refresh = () => { void load(); };
    const timer = window.setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      clearTimeout(initialTimer);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [selected, selectedAvailable, info.box.key, info.cardId, reload]);

  const currentHistory = selected ? inbox.conversations[selected] : undefined;
  const messages = currentHistory ?? loadedMessages;
  const reading = useRef(new Set<number>());
  useEffect(() => {
    if (archived || !selected || !currentHistory || document.hidden) return;
    for (const message of currentHistory) {
      if (
        message.isYou ||
        message.readAt ||
        message.active === false ||
        reading.current.has(message.id)
      )
        continue;
      reading.current.add(message.id);
      void fetch(`/api/box/${encodeURIComponent(info.box.key)}/messages/${message.id}/read`, {
        method: 'PUT', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }, body: '{}',
      })
        .then((result) => {
          if (result.ok) inboxRef.current.markRead(selected, message.id);
          else reading.current.delete(message.id);
        })
        .catch(() => { reading.current.delete(message.id); });
    }
  }, [selected, currentHistory, info.box.key, archived]);

  useEffect(() => {
    const history = historyRef.current;
    if (history) history.scrollTop = history.scrollHeight;
  }, [messages]);

  function cancelEditing() {
    if (editing) setDraft(editing.previousDraft);
    setEditing(null);
  }

  async function changeMessage(message: MessageInfo, value: string) {
    if (archived || !message.isYou || message.active === false || busy || uploading) return;
    if (value && !canEditMessage(message.timestamp)) {
      showToast(t('game.messenger.editExpired'));
      return;
    }
    setBusy(true);
    try {
      const key = info.box.key;
      const result = await santaClient.updateOwnMessage({ key, id: message.id, message: value });
      if (!result.ok) {
        const response = await fetch(`/api/box/${encodeURIComponent(key)}/messages/${message.id}`, {
          method: 'PUT',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'x-santa': 'true' },
          body: JSON.stringify({ message: value }),
        });
        if (!response.ok) throw new Error('Message update failed');
      }
      if (editing?.message.id === message.id) cancelEditing();
      setReload((value) => value + 1);
    } catch {
      showToast(t('game.messenger.updateFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function send(file: UploadedFile | null = pendingFile) {
    const text = file ? file.filename : draft.trim();
    if (
      archived ||
      !thread ||
      recipientUnavailable ||
      !text ||
      busy ||
      (uploading && !file) ||
      loading ||
      loadError
    ) {
      return;
    }
    if (editing && !file) {
      await changeMessage(editing.message, text);
      return;
    }
    setBusy(true);
    setAttachmentError('');
    const key = info.box.key;
    try {
      if (thread.id.startsWith('user-')) {
        const recipients = await santaClient.getCards({ key });
        if (!recipients.ok || !recipients.data?.some((item) => item.id === thread.cardId)) {
          setRecipientUnavailable(true);
          return;
        }
      }
      const res =
        thread.id.startsWith('org-')
          ? await organizerMessages(key, Number(thread.id.slice(4)), { message: text, ...(file ? { attachment: file } : {}) })
          : thread.id === 'org'
          ? await santaClient.writeToBoxOrganizer({
              key,
              message: text,
              ...(file ? { attachment: file } : {}),
            })
          : thread.id === 'me'
            ? await santaClient.writeToSanta({
                key,
                message: text,
                ...(file ? { attachment: file } : {}),
              })
            : thread.id === 'giftee'
              ? await santaClient.writeToWard({
                  key,
                  message: text,
                  ...(file ? { attachment: file } : {}),
                })
              : await santaClient.writeOrgMessageToUser({
                  key,
                  id: thread.cardId ?? Number(thread.id.replace('user-', '')),
                  // Older servers also use the body cardId to load the send response.
                  cardId: thread.cardId ?? Number(thread.id.replace('user-', '')),
                  message: text,
                  ...(file ? { attachment: file } : {}),
                });
      if (!res.ok) {
        if (file) setAttachmentError(t('game.messenger.sendFailed'));
        showToast(t(res.error?.message || 'game.messenger.sendFailed'));
        return;
      }
      if (file) setPendingFile(null);
      else setDraft('');
      setMessages(res.data ?? []);
      if (selected) inboxRef.current.update(selected, res.data ?? []);
    } catch {
      if (file) setAttachmentError(t('game.messenger.sendFailed'));
      showToast(t('game.messenger.sendFailed'));
    } finally {
      setBusy(false);
    }
  }

  const dateFor = (timestamp?: number | string) => {
    if (timestamp == null || timestamp === '') return null;
    // Database timestamps can arrive as strings despite the client's numeric type.
    const numeric = Number(timestamp);
    const date = new Date(Number.isFinite(numeric) ? numeric : timestamp);
    return Number.isFinite(date.getTime()) ? date : null;
  };
  const timeLabel = (timestamp?: number) =>
    dateFor(timestamp)?.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) ||
    '';
  const dateLabel = (timestamp?: number) =>
    dateFor(timestamp)?.toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }) || '';
  const orderedMessages = [...messages].sort(
    (a, b) => (dateFor(a.timestamp)?.getTime() || 0) - (dateFor(b.timestamp)?.getTime() || 0),
  );

  const avatarCards = sortOrganizerCards(cards);
  function threadIcon(item: Thread, compact = false) {
    if (item.id.startsWith('org-')) {
      const organizer = info.organizers?.find(person => person.id === Number(item.id.slice(4)));
      return <OrganizerFace compact={compact} picture={organizer?.picture} index={organizer?.id} />;
    }
    if (item.id === 'org') {
      return (
        <span className="messenger__box-art">
          <BoxPicture picture={info.box.picture} logo={info.box.logo} />
        </span>
      );
    }
    const index = avatarCards.findIndex((person) => person.id === item.cardId);
    const person = index >= 0 ? avatarCards[index] : null;
    if (person) return <ParticipantFace curiositySource="messenger" card={person} index={index} />;
    if (item.id === 'giftee' && ward) {
      const wardIndex = avatarCards.findIndex((person) => person.id === ward.id);
      return <ParticipantFace curiositySource="messenger" isOwn={ward.id === info.cardId} card={ward} index={Math.max(0, wardIndex)} />;
    }
    return item.id === 'me' ? <SantaAvatar /> : <UserIcon />;
  }

  function senderIcon(message: MessageInfo, conversation: Thread) {
    if (!message.isYou) return threadIcon(conversation, true);
    if (conversation.id === 'giftee') return <SantaAvatar />;
    if (conversation.id.startsWith('org-') && info.isAdmin) {
      const own = info.organizers?.find(person => person.isYou);
      if (own) return <OrganizerFace compact picture={own.picture} index={own.id} />;
    }
    if (conversation.id.startsWith('user-')) {
      const ownOrganizer = info.organizers?.find(person => person.isYou);
      if (ownOrganizer) return <OrganizerFace compact picture={ownOrganizer.picture} index={ownOrganizer.id} />;
      return (
        <span className="messenger__box-art">
          <BoxPicture picture={info.box.picture} logo={info.box.logo} />
        </span>
      );
    }
    if (card) {
      const index = avatarCards.findIndex((person) => person.id === card.id);
      return <ParticipantFace card={card} index={Math.max(0, index)} />;
    }
    return <UserIcon />;
  }

  return (
    <GamePageShell
      view="messenger"
      showHeading={mobile && !thread}
      headingClassName="messenger__page-heading"
      testId="game-messenger"
      className={`messenger-screen${thread ? ' messenger-screen--selected' : ''}`}
    >
      <MobileLandscapeBackground />

      <div className={`messenger${thread ? ' messenger--selected' : ''}`}>
        <nav
          className={`messenger__threads${visibleThreads.length ? '' : ' messenger__threads--empty'}`}
          aria-label={t('game.messenger.title')}
          data-testid="game-messenger-list"
        >
          <Stack gap={3}>
            {visibleThreads.length ? (
              visibleThreads.map((item) => {
                const summary = inbox.summaries[item.id];
                const last = summary?.last;
                const unread = summary?.unread ?? 0;
                return (
                  <Panel
                    key={item.id}
                    padding="none"
                    elevated
                    wide
                    className="messenger__thread-panel"
                  >
                    <button
                      type="button"
                      className={`messenger__thread${item.id === selected ? ' messenger__thread--selected' : ''}`}
                      aria-current={item.id === selected ? 'page' : undefined}
                      onClick={() => navigate(boxMessengerPath(info.box.key, item.id))}
                      data-testid={`game-thread-${item.id}`}
                    >
                      <span className="messenger__avatar" aria-hidden>
                        {threadIcon(item)}
                      </span>
                      <span className="messenger__thread-copy">
                        <strong>{item.title}</strong>
                        <span>
                          {last
                            ? `${last.isYou ? t('game.messenger.previewYou') : item.title}: ${last.message?.trim() || t('game.messenger.previewAttachment')}`
                            : t(
                                inbox.loaded
                                  ? 'game.messenger.previewEmpty'
                                  : 'game.messenger.previewLoading',
                              )}
                        </span>
                      </span>
                      {unread > 0 && (
                        <span aria-label={t('game.messenger.unreadCount', { count: unread })}>
                          <NotificationBadge
                            count={unread}
                            testId={`game-thread-${item.id}-unread`}
                          />
                        </span>
                      )}
                      <ChevronRightIcon className="messenger__thread-chevron" />
                    </button>
                  </Panel>
                );
              })
            ) : (
              isAlpine ? (
                <Panel elevated padding="none">
              <p className="messenger__empty-copy" role="status">
                {t(
                  info.isAdmin
                      ? 'game.messenger.emptyOrganizer'
                      : 'game.messenger.empty',
                )}
              </p>
                </Panel>
              ) : (
              <p className="messenger__empty-copy" role="status">
                {t(
                  info.isAdmin
                      ? 'game.messenger.emptyOrganizer'
                      : 'game.messenger.empty',
                )}
              </p>
              )
            )}
          </Stack>
        </nav>
        <Panel
          elevated
          wide
          padding="none"
          role="region"
          className={`messenger__conversation${thread ? '' : ' messenger__conversation--empty'}`}
          edges={thread ? undefined : { pieces: 2 }}
          aria-label={thread?.title || t('game.messenger.title')}
        >
          {thread ? (
            <>
              <header className="messenger__header">
                <Button
                  variant="ghost"
                  size="small"
                  className="messenger__back"
                  aria-label={t('game.messenger.back')}
                  onClick={() => navigate(boxMessengerPath(info.box.key))}
                  testId="game-messenger-back"
                >
                  <span aria-hidden="true">←</span>
                </Button>
                <div className="messenger__identity">
                  <span className="messenger__avatar" aria-hidden>
                    {threadIcon(thread)}
                  </span>
                  <div>
                    <Typography as="h2" role="title/card">
                      {thread.title}
                    </Typography>
                    <Typography as="p" role="body/sm">
                      {thread.hint}
                    </Typography>
                  </div>
                </div>
              </header>
              <div
                className="messenger__history"
                ref={historyRef}
                role="log"
                aria-live="polite"
                aria-label={thread.title}
                aria-busy={loading}
                tabIndex={0}
              >
                {loading ? (
                  <p className="messenger__empty-copy">{t('game.messenger.loading')}</p>
                ) : loadError ? (
                  <div className="messenger__empty-copy">
                    <Note tone="warning">{t('game.messenger.loadFailed')}</Note>
                    <Button variant="secondary" onClick={() => setReload((value) => value + 1)}>
                      {t('game.messenger.retry')}
                    </Button>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="messenger__welcome">
                    <span className="messenger__welcome-icon" aria-hidden>
                      <MailIcon />
                    </span>
                    <Typography as="p" role="body/strong">
                      {t('game.messenger.threadEmpty')}
                    </Typography>
                    <Typography as="p" role="body/sm">
                      {thread.hint}
                    </Typography>
                  </div>
                ) : (
                  orderedMessages.map((item, index) => (
                    <Fragment key={item.id}>
                      {dateLabel(item.timestamp) &&
                      (index === 0 ||
                        dateLabel(orderedMessages[index - 1].timestamp) !==
                          dateLabel(item.timestamp)) ? (
                        <div className="messenger__date">{dateLabel(item.timestamp)}</div>
                      ) : null}
                      {item.active === false ? (
                        <p
                          className={`messenger__deleted${item.isYou ? ' messenger__deleted--outgoing' : ''}`}
                          data-testid={`game-message-${item.id}-deleted`}
                        >
                          {t('game.messenger.deleted')}
                        </p>
                      ) : (
                        <div
                          className={`messenger__message-row${item.isYou ? ' messenger__message-row--outgoing' : ''}`}
                        >
                          <span className="messenger__sender-avatar" aria-hidden="true">
                            {senderIcon(item, thread)}
                          </span>
                          <MessageBubble
                            message={item}
                            disabled={
                              archived || busy || uploading || loading || Boolean(pendingFile)
                            }
                            onEdit={() => {
                              setEditing({
                                message: item,
                                previousDraft: editing?.previousDraft ?? draft,
                              });
                              setDraft(item.message);
                              requestAnimationFrame(() =>
                                document
                                  .querySelector<HTMLTextAreaElement>(
                                    '.messenger__composer textarea',
                                  )
                                  ?.focus(),
                              );
                            }}
                            onDelete={() => void changeMessage(item, '')}
                          >
                            <span className="messenger__sender-label">
                              {item.isYou
                                ? t('game.messenger.you')
                                : item.isOrg
                                  ? thread.title
                                  : thread.title}
                            </span>
                            {item.attachment ? <MessageAttachment file={item.attachment} /> : null}
                            {(!item.attachment || item.message !== item.attachment.filename) &&
                            item.message ? (
                              <p className="messenger__message-text">{item.message}</p>
                            ) : null}
                            <footer
                              className={`messenger__message-meta${item.attachment ? ' messenger__message-meta--attachment' : ''}`}
                            >
                              {item.attachment ? (
                                <>
                                  <span
                                    className="messenger__filename"
                                    title={item.attachment.filename}
                                  >
                                    {item.attachment.filename}
                                  </span>
                                  <MessageAttachment file={item.attachment} compact />
                                </>
                              ) : null}
                              {dateFor(item.timestamp) ? (
                                <time
                                  dateTime={dateFor(item.timestamp)!.toISOString()}
                                  title={`${dateLabel(item.timestamp)} ${timeLabel(item.timestamp)}`}
                                >
                                  {timeLabel(item.timestamp)}
                                </time>
                              ) : null}
                              {item.editedAt ? <span>{t('game.messenger.edited')}</span> : null}
                              {item.isYou ? (
                                <span
                                  className={`messenger__receipt${item.readAt ? ' messenger__receipt--read' : ''}`}
                                  role="img"
                                  title={
                                    item.readAt
                                      ? t('game.messenger.readAt', { time: timeLabel(item.readAt) })
                                      : t('game.messenger.sent')
                                  }
                                  aria-label={
                                    item.readAt
                                      ? t('game.messenger.readAt', { time: timeLabel(item.readAt) })
                                      : t('game.messenger.sent')
                                  }
                                >
                                  <span aria-hidden>
                                    <span>✓</span>
                                    {item.readAt ? <span>✓</span> : null}
                                  </span>
                                </span>
                              ) : null}
                            </footer>
                          </MessageBubble>
                        </div>
                      )}
                    </Fragment>
                  ))
                )}
              </div>
              {archived ? (
                <Typography as="p" role="body/sm" className="messenger__composer">
                  {t('game.messenger.archivedHint')}
                </Typography>
              ) : (
                <form
                  className="messenger__composer"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void send();
                  }}
                >
                  {recipientUnavailable ? (
                    <p role="status">{t('game.messenger.deletedCardHint')}</p>
                  ) : null}
                  {editing ? (
                    <div className="messenger__editing">
                      <span>{t('game.messenger.editing')}</span>
                      <Button variant="ghost" size="small" disabled={busy} onClick={cancelEditing}>
                        {t('game.messenger.cancelEdit')}
                      </Button>
                    </div>
                  ) : null}
                  <FileUpload
                    ref={fileUploadRef}
                    kind="attachment"
                    maxSize={1024 * 1024}
                    tooLargeMessage={t('game.messenger.fileTooLarge')}
                    onChange={(state, file) => {
                      if (state === 'selected') {
                        setUploading(true);
                        setUploadName(file?.filename || '');
                        setAttachmentError('');
                      }
                      if (state === 'uploaded' && file) {
                        setUploading(false);
                        setPendingFile(file);
                        void send(file);
                      }
                      if (state === 'error') {
                        setUploading(false);
                        setAttachmentError(t('game.messenger.uploadFailed'));
                      }
                    }}
                  />
                  {attachmentError ? (
                    <p className="messenger__upload-error" role="alert">
                      {attachmentError}
                    </p>
                  ) : null}
                  {pendingFile || uploading ? (
                    <div className="messenger__pending-file">
                      <span className="messenger__file-icon" aria-hidden>
                        <AttachmentIcon />
                      </span>
                      <span className="messenger__file-copy">
                        <strong>{pendingFile?.filename || uploadName}</strong>
                        <span role={uploading ? 'status' : undefined}>
                          {t(
                            uploading
                              ? 'game.messenger.uploading'
                              : busy
                                ? 'game.messenger.sendingFile'
                                : 'game.messenger.readyToRetry',
                          )}
                        </span>
                      </span>
                      {!uploading ? (
                        <Button
                          variant="ghost"
                          size="small"
                          icon={<CloseIcon />}
                          aria-label={t('game.messenger.removeFile')}
                          title={t('game.messenger.removeFile')}
                          disabled={busy}
                          onClick={() => {
                            setPendingFile(null);
                            setAttachmentError('');
                          }}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  <div className="messenger__send">
                    <div className="messenger__attach-control">
                      <Button
                        variant="soft"
                        icon={<AttachmentIcon />}
                        className="messenger__attach-button"
                        aria-label={t('game.messenger.attach')}
                        title={`${t('game.messenger.attach')} · ${t('game.messenger.fileLimit')}`}
                        loading={uploading}
                        disabled={
                          recipientUnavailable ||
                          busy ||
                          uploading ||
                          loading ||
                          loadError ||
                          Boolean(pendingFile) ||
                          Boolean(editing)
                        }
                        onClick={() => fileUploadRef.current?.open()}
                        testId="game-messenger-attach"
                      />
                    </div>
                    <Textarea
                      value={draft}
                      onValueChange={setDraft}
                      onKeyDown={(event) => {
                        if (
                          event.key !== 'Enter' ||
                          event.nativeEvent.isComposing ||
                          event.keyCode === 229
                        )
                          return;
                        if (event.metaKey) {
                          event.preventDefault();
                          const input = event.currentTarget;
                          const start = input.selectionStart;
                          const end = input.selectionEnd;
                          setDraft(input.value.slice(0, start) + '\n' + input.value.slice(end));
                          requestAnimationFrame(() =>
                            input.setSelectionRange(start + 1, start + 1),
                          );
                        } else if (!event.shiftKey && !event.ctrlKey && !event.altKey) {
                          event.preventDefault();
                          if (!event.repeat) void send();
                        }
                      }}
                      aria-label={t('game.messenger.composer')}
                      placeholder={t('game.messenger.composerPlaceholder', {
                        name:
                          thread.id === 'giftee' ? ward?.username || thread.title : thread.title,
                      })}
                      rows={1}
                      disabled={busy || loading || loadError || recipientUnavailable}
                      testId="game-messenger-draft"
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      loading={busy}
                      disabled={
                        recipientUnavailable ||
                        (!draft.trim() && !pendingFile) ||
                        uploading ||
                        loading ||
                        loadError
                      }
                      icon={<ArrowRightIcon />}
                      aria-label={t(editing ? 'game.messenger.saveEdit' : 'game.messenger.send')}
                      className="messenger__submit"
                      testId="game-messenger-send"
                    >
                      <span className="messenger__submit-label">
                        {t(editing ? 'game.messenger.saveEdit' : 'game.messenger.send')}
                      </span>
                    </Button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div className="messenger__welcome">
              <span className="messenger__welcome-icon" aria-hidden>
                <MailIcon />
              </span>
              <Typography as="h2" role="title/card">
                {t('game.messenger.select')}
              </Typography>
              <Typography as="p" role="body/sm">
                {t(threads.length ? 'game.messenger.subtitle' : 'game.messenger.empty')}
              </Typography>
            </div>
          )}
        </Panel>
      </div>
    </GamePageShell>
  );
}
